import requester from '@/lib/axios';
import { CourseFormatEnum } from '@/models/Course';
import { OrderingFrom, TimeFilter } from '@/models/Filter';
import JsonApiResponse from '@/models/JsonApiResponse';
import Klass, {
  DaysOfWeekTypeEnum,
  KlassPayload,
  KlassPerformanceSummary,
  KlassPendingSummary,
  KlassProgress,
  KlassStatusEnum,
  KlassTypeEnum,
} from '@/models/Klass';
import { KlassGroup } from '@/models/KlassGroup';
import { BaseFilters } from '@/models/Service';
import { StudentsPerformance } from '@/models/Statistics';
import { AxiosResponse } from 'axios';

export const KLASS_ORDERING_FIELDS = [
  'klass_start_date',
  'klass_end_date',
  'day_time',
] as const;

export type orderingKlassDate = OrderingFrom<typeof KLASS_ORDERING_FIELDS>;

export type ListKlassesFilters = BaseFilters & {
  name?: string;
  teacherId?: number[];
  unitId?: number[];
  ordering?: orderingKlassDate;
  studentId?: number;
  courseId?: number[];
  enrollmentStatus?: string[];
  courseLineId?: number[];
  hasAbsenceNotification?: boolean;
  dayOfWeek?: Array<DaysOfWeekTypeEnum>;
  courseFormats?: Array<CourseFormatEnum>;
  endSoon?: boolean;
  startSoon?: boolean;
  hasActiveLessons?: boolean;
  timeAfter?: TimeFilter;
  timeBefore?: TimeFilter;
  status?: Array<KlassStatusEnum>;
  klassType?: Array<KlassTypeEnum>;
  id?: Array<number>;
};

export async function getKlass(klassId: number) {
  const { data } = await requester().get<Klass>(`classes/${klassId}/`);

  return data;
}

export const listKlasses = async (params: ListKlassesFilters = {}) => {
  const { data } = await requester().get<JsonApiResponse<Klass>>('classes/', {
    params,
  });

  return data;
};

export const listKlassesPerformanceSummary = async (
  params: ListKlassesFilters = {},
) => {
  const { data } = await requester().get<
    JsonApiResponse<KlassPerformanceSummary>
  >('classes/performance-summary/', { params });

  return data;
};

export const listKlassesPendingSummary = async (
  params: ListKlassesFilters = {},
) => {
  const { data } = await requester().get<JsonApiResponse<KlassPendingSummary>>(
    'classes/pending-summary/',
    { params },
  );

  return data;
};

export async function getKlassPerformance(klassId: number) {
  const { data } = await requester().get<StudentsPerformance>(
    `classes/${klassId}/performance/`,
  );
  return data;
}

export async function getKlassProgressById(klassId?: number) {
  const { data } = await requester().get<KlassProgress>(
    `classes/${klassId}/progress/`,
  );
  return data;
}

export const updateKlass = async (
  klassId: number,
  parcialKlass: Partial<Klass>,
) => {
  const { data } = await requester().patch(`classes/${klassId}/`, parcialKlass);
  return data;
};

export const updateKlassStartDate = async (
  klassId: number,
  body: Pick<Klass, 'klassStartDate'>,
) => {
  type Response = AxiosResponse<Pick<Klass, 'klassStartDate'>>;
  const { data: klass } = await requester().patch<UpdateKlassStatus, Response>(
    `classes/${klassId}/klass-start-date/`,
    body,
  );
  return klass;
};

type UpdateKlassStatus = Pick<Klass, 'status'>;

export const updateKlassStatus = async (
  klassId: number,
  body: UpdateKlassStatus,
) => {
  type Response = AxiosResponse<Klass>;
  const { data: klass } = await requester().patch<UpdateKlassStatus, Response>(
    `classes/${klassId}/status/`,
    body,
  );
  return klass;
};

export const createKlass = async (klass: KlassPayload) => {
  const { data } = await requester().post<Klass>(`classes/`, klass);
  return data;
};

export async function getKlassGroup(klassId: number) {
  const { data } = await requester().get<KlassGroup>(
    `classes/${klassId}/group/`,
  );
  return data;
}

export async function updateKlassCoursePath(klassId: number) {
  const { data } = await requester().patch(
    `classes/${klassId}/update-course-path/`,
  );
  return data;
}
